import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

export type TargetVisibilityState = {
  isVisible: boolean;
  isAboveViewport: boolean;
};

function computeFromRect(rect: DOMRect): TargetVisibilityState {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const isVisible = rect.bottom > 0 && rect.right > 0 && rect.top < viewportHeight && rect.left < viewportWidth;
  return { isVisible, isAboveViewport: !isVisible && rect.bottom <= 0 };
}

export function useIsTargetVisible(ref: RefObject<Element | null>): TargetVisibilityState {
  const [state, setState] = useState<TargetVisibilityState>({ isVisible: true, isAboveViewport: false });

  // Sync layout read before first paint so a page already scrolled past the
  // target doesn't render with the wrong state until IntersectionObserver fires.
  useLayoutEffect(() => {
    const target = ref.current;
    if (target) setState(computeFromRect(target.getBoundingClientRect()));
  }, [ref]);

  useEffect(() => {
    const target = ref.current;
    if (!target || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          setState({
            isVisible: entry.isIntersecting,
            isAboveViewport: !entry.isIntersecting && entry.boundingClientRect.bottom <= 0,
          });
        }
      },
      { threshold: 0 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [ref]);

  return state;
}
